import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, Instagram, Clock, MessageCircle, Send } from "lucide-react";
import { BRAND, buildWhatsAppLink } from "../lib/brand";
import { useReveal } from "../hooks/useReveal";

export default function Contact() {
  const ref = useReveal();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.message) {
      toast.error("Please share your name and message.");
      return;
    }
    setSubmitting(true);
    const text =
      `Hello Qamar Al Zahran!\n\n` +
      `Name: ${form.name}\n` +
      (form.email ? `Email: ${form.email}\n` : "") +
      (form.phone ? `Phone: ${form.phone}\n` : "") +
      `\n${form.message}`;
    window.open(buildWhatsAppLink(text), "_blank", "noopener");
    toast.success("Opening WhatsApp…");
    setForm({ name: "", email: "", phone: "", message: "" });
    setSubmitting(false);
  };

  const infoItems = [
    { icon: Phone, label: "Phone", value: BRAND.phone, href: `tel:${BRAND.phone}`, testId: "contact-phone" },
    { icon: Mail, label: "Email", value: BRAND.email, href: `mailto:${BRAND.email}`, testId: "contact-email" },
    { icon: Instagram, label: "Instagram", value: `@${BRAND.instagram}`, href: BRAND.instagramUrl, testId: "contact-instagram" },
    { icon: Clock, label: "Opening Hours", value: BRAND.hours, testId: "contact-hours" },
  ];

  return (
    <div ref={ref} data-testid="contact-page" className="pt-28 pb-24 bg-brand-ivory min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-14 reveal">
          <span className="text-[11px] tracking-[0.32em] uppercase text-brand-gold font-semibold">Get in touch</span>
          <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-brand-brown leading-tight">
            We&apos;d love to hear from you
          </h1>
          <p className="mt-4 text-brand-muted max-w-2xl mx-auto">
            Send us a message — we&apos;ll reply on WhatsApp, usually within minutes during opening hours.
          </p>
        </header>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          <aside className="reveal bg-brand-brown text-brand-ivory rounded-3xl p-8 lg:p-10 relative overflow-hidden grain">
            <h2 className="font-serif-display text-2xl sm:text-3xl text-brand-gold">Visit & Connect</h2>
            <p className="mt-3 text-brand-ivory/75 text-sm leading-relaxed">
              Find us at the Qamar Al Zahran Roastery, or reach out below — we&apos;re here to help with
              orders, gifting consultations, and bespoke requests.
            </p>

            <ul className="mt-8 space-y-5">
              {infoItems.map((it) => {
                const Inner = (
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-full bg-brand-gold/15 text-brand-gold flex items-center justify-center shrink-0">
                      <it.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.24em] uppercase text-brand-ivory/60">{it.label}</div>
                      <div className="text-sm text-brand-ivory">{it.value}</div>
                    </div>
                  </div>
                );
                return (
                  <li key={it.label} data-testid={it.testId}>
                    {it.href ? (
                      <a href={it.href} target={it.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="block hover:translate-x-1 transition-transform">
                        {Inner}
                      </a>
                    ) : (
                      Inner
                    )}
                  </li>
                );
              })}
            </ul>

            <a
              href={buildWhatsAppLink("Hello Qamar Al Zahran! I have a question.")}
              target="_blank" rel="noreferrer"
              data-testid="contact-direct-whatsapp"
              className="mt-10 inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#1DA851] transition-colors"
            >
              <MessageCircle className="h-4 w-4" />
              Chat directly on WhatsApp
            </a>
          </aside>

          <form onSubmit={handleSubmit} data-testid="contact-form" className="reveal bg-white rounded-3xl p-8 lg:p-10 shadow-[0_12px_40px_rgba(59,30,26,0.08)]">
            <h2 className="font-serif-display text-2xl sm:text-3xl text-brand-brown">Send us a message</h2>
            <p className="mt-2 text-sm text-brand-muted">We&apos;ll continue the conversation on WhatsApp.</p>

            <div className="mt-7 space-y-4">
              <div>
                <label className="text-xs tracking-wide uppercase text-brand-muted">Your Name *</label>
                <input
                  data-testid="contact-name-input"
                  name="name" value={form.name} onChange={handleChange} required
                  className="mt-1 w-full bg-brand-ivory border border-brand-beige rounded-xl px-4 py-3 text-brand-brown focus:outline-none focus:border-brand-gold transition-colors"
                  placeholder="Aisha Al-Mansoori"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs tracking-wide uppercase text-brand-muted">Email</label>
                  <input
                    data-testid="contact-email-input"
                    name="email" type="email" value={form.email} onChange={handleChange}
                    className="mt-1 w-full bg-brand-ivory border border-brand-beige rounded-xl px-4 py-3 text-brand-brown focus:outline-none focus:border-brand-gold transition-colors"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label className="text-xs tracking-wide uppercase text-brand-muted">Phone</label>
                  <input
                    data-testid="contact-phone-input"
                    name="phone" value={form.phone} onChange={handleChange}
                    className="mt-1 w-full bg-brand-ivory border border-brand-beige rounded-xl px-4 py-3 text-brand-brown focus:outline-none focus:border-brand-gold transition-colors"
                    placeholder="+971 ..."
                  />
                </div>
              </div>
              <div>
                <label className="text-xs tracking-wide uppercase text-brand-muted">Message *</label>
                <textarea
                  data-testid="contact-message-input"
                  name="message" value={form.message} onChange={handleChange} required rows={5}
                  className="mt-1 w-full bg-brand-ivory border border-brand-beige rounded-xl px-4 py-3 text-brand-brown focus:outline-none focus:border-brand-gold transition-colors resize-none"
                  placeholder="Tell us what you&apos;re looking for…"
                />
              </div>
            </div>

            <button
              type="submit" disabled={submitting}
              data-testid="contact-submit-button"
              className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-brand-brown text-brand-ivory py-3.5 rounded-full font-semibold hover:bg-[#5A2F2A] transition-all hover:shadow-lg disabled:opacity-60"
            >
              {submitting ? "Opening…" : (<><Send className="h-4 w-4 text-brand-gold" /> Send on WhatsApp</>)}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
